/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { ArrowRight, ChevronDown, MenuIcon, XIcon } from "lucide-react";
import React, { useState } from "react";
import Button from "@/components/atoms/buttons/Button";
import { Separator } from "@radix-ui/react-dropdown-menu";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@radix-ui/react-accordion";
import Link from "next/link";
import NavLink from "@/components/atoms/navbar/nav-link/NavLink";
import LanguageSwitcher from "@/components/organisms/Header/LanguageSwitcher";
import { AllDocumentTypes } from "@/prismicio-types";
import { Locales } from "@/lib/i18n";
import Image from "next/image";
import { LinkField } from "@prismicio/client";
import { __, getPermalink } from "@/lib/i18n/functions";
import CartButton from "@/components/molecules/Cart/cart-button";
import { cn, getHomePageSlug } from "@/lib/utils";
import { usePathname } from "next/navigation";
import WishlistButton from "@/components/atoms/navbar/wishlist-button";
import LoginNavbarButton from "@/components/atoms/navbar/login-navbar-button";

type Category = {
  label: string | undefined;
  link: LinkField & { type?: string; slug?: string; uid?: string };
};

type MobileMenuProps = {
  categories: Category[];
  document?: AllDocumentTypes;
  locale: Locales;
  resolution?: {
    isArchive: boolean;
    templateName: string;
    slugs: any;
    isStatic: boolean;
  };
  alternates?: Record<string, string>;
};

const MobileMenu = ({ categories, document, locale, resolution, alternates }: MobileMenuProps) => {
  const pathname = usePathname();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <div className="relative flex flex-col px-0 pt-0 xl:hidden">
      <div className="relative z-50 flex w-full items-center justify-between bg-white px-6 py-3">
        <Button
          variant="light"
          className="flex h-auto items-center gap-2.5 px-[9px] py-2.5 text-sm font-medium"
          onClick={toggleMenu}
        >
          {isMenuOpen ? <XIcon className="h-3.5 w-3.5" /> : <MenuIcon className="h-3.5 w-3.5" />}
          Menu
        </Button>

        <div className="relative flex items-center gap-2">
          <LoginNavbarButton locale={locale} />
          <CartButton locale={locale} />
        </div>

        <figure className="absolute top-1/2 left-1/2 m-0 -translate-x-1/2 -translate-y-1/2">
          <Image src="/icons/Festi-logo.svg" alt="FESTI Logo" className="h-9 w-[39px]" width={39} height={36} />
          <Link href={getHomePageSlug(locale)} className="absolute inset-0">
            <span className="sr-only">{__("Navigate Home", locale)}</span>
          </Link>
        </figure>
      </div>

      <div
        onClick={toggleMenu}
        className={`fixed inset-0 z-40 bg-black/50 transition-opacity duration-300 ease-in-out ${
          isMenuOpen ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
        }`}
      />

      {/* Animated content */}
      <nav
        className={`z-50 overflow-hidden bg-white transition-[max-height] duration-500 ease-in-out ${
          isMenuOpen ? "max-h-[1000px]" : "max-h-0"
        }`}
      >
        <ul className="flex w-full flex-col items-center gap-4.5 px-5 pt-4.5 pb-5">
          <li className="w-full">
            <hr className="mb-4.5 h-px w-full bg-slate-100" />
            <Link href={getPermalink(locale, "catalogue")} className="flex w-full items-center justify-between">
              <span className="font-cabinet-grotesk text-dark-primary text-2xl font-medium">
                {__("Catalogue", locale)}
              </span>
              <ArrowRight className="text-primary-light h-6 w-6" />
            </Link>
          </li>

          <li className="w-full">
            <Separator className="mb-[18px] h-px w-full bg-slate-100" />
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="categories" className="group border-0">
                <AccordionTrigger className="flex w-full cursor-pointer items-center justify-between py-0 hover:no-underline">
                  <span className="font-cabinet-grotesk text-dark-primary text-2xl font-medium">
                    {__("Categories", locale)}
                  </span>
                  <ChevronDown className="text-primary-light h-6 w-6 transition-transform duration-300 ease-in-out group-data-[state=open]:rotate-180" />
                </AccordionTrigger>
                <AccordionContent>
                  <div className="mt-4 flex flex-col gap-2">
                    {categories.map((category, index) => {
                      const isAlwaysPink = category.link.type === "tag";

                      const { type, uid } = category.link;
                      const segment = type === "tag" ? "tag" : "categories";
                      const href = `${getPermalink(locale, segment)}/${uid}`;

                      const isSelected = pathname === href;

                      const textColorClass = isSelected
                        ? "text-[#0093d0] font-bold"
                        : isAlwaysPink
                          ? "text-[#ee4197] font-semibold"
                          : "text-dark-primary font-medium";

                      return (
                        <Link
                          key={index}
                          href={href}
                          className={cn(
                            "font-geist flex items-center",
                            textColorClass,
                            !isAlwaysPink && "hover:text-blue-brand"
                          )}
                        >
                          {category.label}
                        </Link>
                      );
                    })}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </li>

          <li className="w-full">
            <hr className="mb-[18px] h-px w-full bg-slate-100" />
            <Link
              href={getPermalink(locale, "inspiration_archive")}
              className="flex w-full items-center justify-between"
            >
              <span className="font-cabinet-grotesk text-dark-primary text-2xl font-medium">
                {__("Inspirations", locale)}
              </span>
              <ArrowRight className="text-primary-light h-6 w-6" />
            </Link>
          </li>

          <hr className="m h-px w-full bg-slate-100" />
          <div className="flex w-full flex-col items-start gap-3">
            {/* <NavLink
              navLink={{ label: __("My favorites", locale), link: getPermalink(locale, "account", "wishlist") }}
              count={24}
            /> */}
            <WishlistButton locale={locale} />
            <NavLink navLink={{ label: __("Contact", locale), link: getPermalink(locale, "contact") }} />
            {document && locale && resolution && (
              <LanguageSwitcher
                document={document}
                locale={locale}
                archive={resolution.isArchive}
                template={resolution.templateName}
                slugs={resolution.slugs}
                isStatic={resolution.isStatic}
                alternates={alternates}
              />
            )}
          </div>
        </ul>
      </nav>
    </div>
  );
};

export default MobileMenu;
